import React, { useState } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";

import LocationSelectComponent from "../common/LocationSelectComponent";
import { uploadBulkUsersApi } from "../../redux/bulkUser/bulkUserApi";
import { API } from "../../redux/auth/authApi";

const BulkUserUpload = ({ refreshUsers }) => {

    const [show, setShow] = useState(false);
    const [location, setLocation] = useState(null);
    const [file, setFile] = useState(null);

    const [loading, setLoading] = useState(false);

    const validate = () => {
        if (!location) {
            toast.error("Location is required");
            return false;
        }

        if (!file) {
            toast.error("File is required");
            return false;
        }

        if (!file.name.endsWith(".xlsx")) {
            toast.error("Only .xlsx file allowed");
            return false;
        }

        if (file.size > 5 * 1024 * 1024) {
            toast.error("Max file size 5MB");
            return false;
        }

        return true;
    };

    const handleDownload = async () => {
        try {
            const res = await API.get("/bulk/user/sample", {
                responseType: "blob",
            });

            const disposition = res.headers["content-disposition"];
            let fileName = "sample.xlsx";

            if (disposition) {
                const match = disposition.match(/filename="?(.+?)"?$/);
                if (match?.[1]) fileName = match[1];
            }

            const url = window.URL.createObjectURL(res.data);

            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();

            link.remove();
            window.URL.revokeObjectURL(url);

            toast.success("Sample downloaded");

        } catch (err) {
            console.error(err);
            toast.error("Download failed");
        }
    };

    const handleUpload = async () => {
        if (!validate()) return;

        try {
            setLoading(true);

            const formData = new FormData();
            formData.append("locationId", location.value);
            formData.append("file", file);

            const res = await uploadBulkUsersApi(formData);
            const data = res.data;

            const successCount = data?.data?.user?.total || 0;
            const duplicateCount = data?.data?.duplicate?.total || 0;
            const duplicateEmails = data?.data?.duplicate?.emails || [];


            if (successCount > 0 && duplicateCount === 0) {
                toast.success(`${successCount} users uploaded successfully`);
            }

            else if (successCount === 0 && duplicateCount > 0) {
                toast.error(
                    `${duplicateCount} duplicate emails found:\n${duplicateEmails.join(", ")}`
                );
            }

            else if (successCount > 0 && duplicateCount > 0) {
                toast.warning(
                    `${successCount} users uploaded, ${duplicateCount} duplicates:\n${duplicateEmails.join(", ")}`
                );
            }

            else {
                toast.info("No users processed");
            }

            setShow(false);
            setFile(null);
            setLocation(null);

            refreshUsers && refreshUsers();

        } catch (err) {
            console.error(err);

            const message =
                err?.response?.data?.message ||
                err?.message ||
                "Upload failed";

            toast.error(message);

        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <button
                className="btn btn-success btn-sm d-flex align-items-center gap-2"
                onClick={() => setShow(true)}
            >
                <Icon icon="mdi:upload" />
                Bulk Upload
            </button>

            {show && (
                <div className="modal fade show d-block">
                    <div className="modal-dialog modal-md modal-dialog-centered">
                        <div className="modal-content radius-16 shadow-lg">

                            <div className="modal-header border-0 pb-0">
                                <h5 className="fw-semibold">Bulk User Upload</h5>
                                <button className="btn-close" onClick={() => setShow(false)} />
                            </div>

                            <div className="modal-body">

                                <button
                                    className="btn btn-outline-primary w-100 mb-2"
                                    onClick={handleDownload}
                                >
                                    Download Sample
                                </button>

                                <p className="text-sm text-muted mb-3">
                                    Download sample → Remove dummy row → Keep headers unchanged.
                                </p>

                                <div className="mb-3">
                                    <LocationSelectComponent
                                        label="Location"
                                        required
                                        value={location}
                                        onChange={(val) => setLocation(val)}
                                    />
                                </div>

                                <div className="border rounded p-3 text-center bg-light">

                                    <input
                                        type="file"
                                        accept=".xlsx"
                                        className="form-control"
                                        onChange={(e) => setFile(e.target.files[0])}
                                    />

                                    {file && (
                                        <div className="mt-2 text-success small">
                                            Selected: {file.name}
                                        </div>
                                    )}

                                </div>

                            </div>

                            <div className="modal-footer border-0">

                                <button
                                    className="btn btn-light"
                                    onClick={() => setShow(false)}
                                >
                                    Cancel
                                </button>

                                <button
                                    className="btn btn-success"
                                    onClick={handleUpload}
                                    disabled={loading}
                                >
                                    {loading ? "Uploading..." : "Upload Users"}
                                </button>

                            </div>

                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default BulkUserUpload;